import {
  BRAND_DOMAIN_MAP,
  CatInAFlatDomain,
  DOG_BUDDY_DOMAINS,
  MadPawsDomain,
} from '@rover/constants/src/platform/domains.constants';
import { decodeHtmlAttribute } from '@rover/utilities/decodeHtmlAttribute';
import { matchesAnyDomain } from '@rover/utilities/getRoverBrand/domain-utils';

import { logRemovedLink } from './logRemovedLink';

const LOCAL_HOSTS: string[] = [
  'rover.local',
  CatInAFlatDomain.Local,
  MadPawsDomain.Local,
  'localhost',
  '127.0.0.1',
];
const BRAND_DOMAINS = [
  ...Object.values(BRAND_DOMAIN_MAP).flatMap((domains) => domains.map(({ value }) => value)),
  ...DOG_BUDDY_DOMAINS,
];
const ALLOWED_HOSTED_PROTOCOLS = ['http:', 'https:'];
const ALLOWED_HOSTLESS_PROTOCOLS = ['mailto:', 'tel:'];
// Relative links resolve against an allowed host.
const RELATIVE_URL_BASE = 'https://www.rover.com';

// Keeps allowed links exactly as written and removes disallowed ones whole, text included.
const LINK_REGEX = /<a\s(?:[^>]*?\s)?href="([^"]*)".*?<\/a>/gis;

// The \1 backreference closes each banned tag with its own name.
const BANNED_TAG_REGEX =
  /<(script|iframe|object|embed|applet|form|input|button|meta|style)[^>]*>.*?<\/\1>/gis;

const parseHref = (href: string): URL | undefined => {
  try {
    return new URL(decodeHtmlAttribute(href), RELATIVE_URL_BASE);
  } catch (e) {
    return undefined;
  }
};

const isAllowedUrl = ({ protocol, hostname }: URL): boolean => {
  if (!hostname) {
    return ALLOWED_HOSTLESS_PROTOCOLS.includes(protocol);
  }
  return (
    ALLOWED_HOSTED_PROTOCOLS.includes(protocol) &&
    (LOCAL_HOSTS.includes(hostname) || matchesAnyDomain(hostname, BRAND_DOMAINS))
  );
};

export const removeDisallowedLinks = (html: string): string =>
  html.replace(LINK_REGEX, (match, href: string) => {
    const url = parseHref(href);
    if (url && isAllowedUrl(url)) {
      return match;
    }
    logRemovedLink(
      url ? { protocol: url.protocol, hostname: url.hostname } : { href: 'unparseable' }
    );
    return '';
  });

export const filterBannedTags = (html: string): string => html.replace(BANNED_TAG_REGEX, '');

export const sanitizeHTML = (html: string): string => {
  return removeDisallowedLinks(filterBannedTags(html));
};
